import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmModal,
  Input,
  ProviderLogo,
  Table,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useOrgMcpServers,
  useDeleteOrgMcpServer,
} from '@/hooks/useOrgMcpServers';
import { useMcpProviders } from '@/hooks/useMcpProviders';
import { routes } from '@/lib/routes';
import { formatRelativeTime } from '../activity/utils';
import { McpProviderPicker } from '../tools/mcp/McpProviderPicker';
import { deriveMcpStatus } from '@/types/mcp';
import type { OrgMcpServer, McpProviderCatalogEntry } from '@/types/mcp';
import type { ProviderSummary } from '@/types/identity';
import { useOrgCredentials } from '@/hooks/useIdentityCredentials';

/**
 * Derive the client-config label from optr-identity's provider summary — the
 * OAuth app lives there (a papp row keyed by serverId), never on the
 * agent-core server config.
 */
function clientLabel(
  serverId: string,
  providers: ProviderSummary[] | undefined,
): string {
  const app = providers?.find((p) => p.providerInstanceId === serverId);
  return app?.oauthApp ? 'Manual' : 'Not configured';
}

export default function OrgMcpServerList() {
  const { data: credentials } = useOrgCredentials();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { data, isLoading } = useOrgMcpServers();
  const { data: catalog } = useMcpProviders();
  const deleteServer = useDeleteOrgMcpServer();

  const providersById = useMemo(() => {
    const m = new Map<string, McpProviderCatalogEntry>();
    for (const p of catalog?.providers ?? []) m.set(p.providerId, p);
    return m;
  }, [catalog]);

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    server: OrgMcpServer | null;
  }>({ isOpen: false, server: null });

  const servers = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(() => {
    if (!searchQuery) return servers;
    const q = searchQuery.toLowerCase();
    return servers.filter(
      (s) => s.name.toLowerCase().includes(q) || s.serverUrl.toLowerCase().includes(q),
    );
  }, [servers, searchQuery]);

  const handleDelete = async () => {
    if (!deleteModal.server) return;
    try {
      await deleteServer.mutateAsync(deleteModal.server.serverId);
      success('Organization MCP server deleted');
      setDeleteModal({ isOpen: false, server: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete server');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (s: OrgMcpServer) => {
        const p = s.providerId ? providersById.get(s.providerId) : undefined;
        return (
          <div className="flex items-center gap-3">
            {s.providerId && (
              <ProviderLogo
                slug={s.providerId}
                displayName={p?.displayName}
                className="h-8 w-8"
                grayscale
              />
            )}
            <div className="min-w-0">
              <p className="font-medium text-foreground">{s.name}</p>
              <p className="text-muted-foreground text-xs mt-0.5 truncate max-w-xs">
                {s.providerId
                  ? (p?.displayName ?? 'Unknown provider')
                  : s.serverUrl}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'sa',
      header: 'Service account',
      render: (s: OrgMcpServer) =>
        s.serviceAccount?.configured ? (
          <Badge variant="success">Configured</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: 'client',
      header: 'Client',
      render: (s: OrgMcpServer) => (
        <span className="text-sm text-muted-foreground">{clientLabel(s.serverId, credentials?.providers)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s: OrgMcpServer) => {
        const status = deriveMcpStatus(s);
        return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
      },
    },
    {
      key: 'lastSync',
      header: 'Last sync',
      render: (s: OrgMcpServer) => (
        <span className="text-sm text-muted-foreground">
          {s.lastSyncedAt ? formatRelativeTime(s.lastSyncedAt) : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (s: OrgMcpServer) => (
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, server: s });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <p className="text-sm text-muted-foreground">
        Install an MCP server once at the organization level — it's
        automatically available in every space. A space-level install of the
        same vendor takes precedence in that space.
      </p>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Available providers
        </h2>
        <McpProviderPicker
          providers={catalog?.providers ?? []}
          customServerAllowed={catalog?.customServerAllowed ?? true}
          onSelectProvider={(id) =>
            navigate(`${routes.console.org.mcpServerNew}?provider=${id}`)
          }
          onSelectCustom={() =>
            navigate(`${routes.console.org.mcpServerNew}?provider=custom`)
          }
        />
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Configured servers
        </h2>
        <div className="flex gap-4 items-center">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search MCP servers..."
              value={searchQuery}
              onChange={(e: { target: { value: string } }) =>
                setSearchQuery(e.target.value)
              }
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={filtered}
          keyExtractor={(s) => s.serverId}
          onRowClick={(s) => navigate(routes.console.org.mcpServer(s.serverId))}
          isLoading={isLoading}
          emptyMessage={
            searchQuery
              ? 'No MCP servers match your search.'
              : 'No organization MCP servers configured. Pick a provider above to get started.'
          }
        />
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, server: null })}
        onConfirm={handleDelete}
        title="Delete organization MCP server"
        message={`Are you sure you want to delete "${deleteModal.server?.name}"? This removes its tools from every space in the organization and revokes all per-user connections.`}
        confirmText="Delete"
        isLoading={deleteServer.isPending}
      />
    </div>
  );
}
